/* =====================================================================================================================
   Atelier Alpino (owner, 04/10): «Visti di recente» in the open search, as in the prototype (design/skisises-luxe,
   assets/js/client/40-wishlist-search.js): under the suggestions, up to six products viewed on this device; when fewer
   have been viewed, the bestsellers fill the row, each group under its own label.

   Nothing is asked before the search opens. Then the tiles come from the Section Rendering API
   (sections/luxe-search-recent.liquid): the viewed products (Prestige's theme:recently-viewed-products, written by
   sections/main-product.liquid) through the search, the bestsellers through the all-products collection sorted by
   best-selling, both requests at once when both are needed. They stay in sessionStorage for 30 minutes, per language,
   currency and country, so the next openings, on any page, show the row at once. Left out: the page's product, sold-out
   products and gift cards (the section), and a bestseller of a model already in the row or of the page's model.
   While the tiles load, placeholders of the same size hold the row's room. The block hides as soon as something is
   typed (the predictive results take its place) and comes back when the field is empty. The tiles are plain links.
   ===================================================================================================================== */
(() => {
  'use strict';

  if (window.luxeSearchRecent) return;
  window.luxeSearchRecent = true;

  const SECTION = 'luxe-search-recent';
  const VIEWED_KEY = 'theme:recently-viewed-products';
  const ROW = 6;
  const ASK = 10; // viewed products asked for: some may have sold out since
  const TTL = 30 * 60 * 1000;

  /* the stylesheet comes without holding up the page (media="print" in snippets/header-search.liquid): it applies once
     it has arrived, and the block shows only when its rules are there (a stylesheet that failed shows nothing) */
  const sheet = document.getElementById('luxe-search-recent-css');
  const settled = new Promise((resolve) => {
    if (!sheet) return resolve();
    const settle = () => {
      sheet.media = 'all';
      resolve();
    };
    if (sheet.sheet) return settle();
    sheet.addEventListener('load', settle, { once: true });
    sheet.addEventListener('error', settle, { once: true });
  });
  const styled = async (root) => {
    await settled;
    return getComputedStyle(root).getPropertyValue('--luxe-search-recent').trim() === '1';
  };

  const esc = (text) => String(text || '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  const words = (text) => String(text || '').split(/\s+/).filter(Boolean);

  /* ---------- the session's tiles: { at, tiles: { id: tile | false }, best: [tile] | null } ---------- */
  let cache = null;

  function storageKey() {
    const shop = window.Shopify || {};
    return [SECTION, 'v1', shop.routes?.root || '/', shop.locale || document.documentElement.lang, shop.currency?.active || '', shop.country || ''].join('|');
  }

  function session() {
    if (cache && Date.now() - cache.at < TTL) return cache;
    cache = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey()) || 'null');
      if (saved && saved.tiles && Date.now() - saved.at < TTL) cache = saved;
    } catch (error) {
      /* storage blocked */
    }
    if (!cache) cache = { at: Date.now(), tiles: {}, best: null };
    return cache;
  }

  function save() {
    try {
      sessionStorage.setItem(storageKey(), JSON.stringify(cache));
    } catch (error) {
      /* storage blocked or full: this page keeps them */
    }
  }

  function viewedIds(current) {
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    } catch (error) {
      /* storage blocked */
    }
    const list = [];
    for (const raw of Array.isArray(ids) ? ids : []) {
      const id = String(raw);
      if (/^\d+$/.test(id) && id !== current && !list.includes(id)) list.push(id);
      if (list.length >= ASK) break;
    }
    return list;
  }

  async function askTiles(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`luxe-search-recent: HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-luxe-search-tiles] > li[data-id]'), (item) => ({
      id: item.dataset.id,
      family: words(item.dataset.family),
      html: item.outerHTML,
    }));
  }

  /* the viewed products first, then the bestsellers not of a model already there */
  function pick(store, ids, skip) {
    const viewed = ids.map((id) => store.tiles[id]).filter(Boolean).slice(0, ROW);
    const taken = new Set(skip);
    const take = (tile) => {
      taken.add(tile.id);
      tile.family.forEach((id) => taken.add(id));
    };
    viewed.forEach(take);

    const best = [];
    for (const tile of store.best || []) {
      if (viewed.length + best.length >= ROW) break;
      if (taken.has(tile.id) || tile.family.some((id) => taken.has(id))) continue;
      best.push(tile);
      take(tile);
    }
    return { viewed, best };
  }

  /* ---------- the block ---------- */
  const WAITING_TILE = '<li><span class="luxe-search-recent__tile"><span class="luxe-search-recent__media"></span><span class="luxe-search-recent__brand">&nbsp;</span><span class="luxe-search-recent__title">&nbsp;</span><span class="luxe-search-recent__price">&nbsp;</span></span></li>';

  function sync(root) {
    const input = root.closest('header-search')?.querySelector('input[name="q"]');
    const typed = Boolean(input && input.value.trim());
    root.hidden = typed || !['waiting', 'ready'].includes(root.dataset.state);
  }

  function paint(root, html, state, signature) {
    root.innerHTML = html;
    root.dataset.state = state;
    root.dataset.signature = signature;
    sync(root);
  }

  function waiting(root) {
    paint(root, `<div class="luxe-search-recent__row luxe-search-recent__row--waiting" aria-hidden="true"><div class="luxe-search-recent__group" style="--luxe-span: ${ROW}"><p class="luxe-search__label luxe-search-recent__label">&nbsp;</p><ul class="luxe-search-recent__list unstyled-list" role="list">${WAITING_TILE.repeat(ROW)}</ul></div></div>`, 'waiting', '');
  }

  function group(id, label, tiles) {
    if (!tiles.length) return '';
    return `<div class="luxe-search-recent__group" style="--luxe-span: ${tiles.length}">`
      + `<p class="luxe-search__label luxe-search-recent__label" id="${id}">${esc(label)}</p>`
      + `<ul class="luxe-search-recent__list unstyled-list" role="list" aria-labelledby="${id}">${tiles.map((tile) => tile.html).join('')}</ul>`
      + '</div>';
  }

  function show(root, { viewed, best }) {
    const signature = [...viewed, ...best].map((tile) => tile.id).join(',');
    if (!signature) return paint(root, '', 'empty', '');
    if (root.dataset.state === 'ready' && root.dataset.signature === signature) return sync(root);
    const id = root.id || SECTION;
    return paint(root, `<div class="luxe-search-recent__row">${group(`${id}-viewed`, root.dataset.labelViewed, viewed)}${group(`${id}-bestsellers`, root.dataset.labelBestsellers, best)}</div>`, 'ready', signature);
  }

  async function fill(root) {
    if (!(await styled(root))) return;
    const store = session();
    const current = root.dataset.productId || String(window.ShopifyAnalytics?.meta?.product?.id || '');
    const skip = [current, ...words(root.dataset.productFamily)].filter(Boolean);
    const ids = viewedIds(current);
    const unknown = ids.filter((id) => !(id in store.tiles));
    const known = ids.filter((id) => store.tiles[id]).length;
    const short = () => pick(store, ids, skip).viewed.length < ROW;

    if (!unknown.length && (store.best || !short())) return show(root, pick(store, ids, skip));
    if (root.dataset.state !== 'ready') waiting(root);

    let bestAsked = false;
    const askBest = () => {
      bestAsked = true;
      const params = new URLSearchParams({ sort_by: 'best-selling', section_id: SECTION });
      return askTiles(`${root.dataset.bestsellersUrl}?${params}`).then((tiles) => {
        store.best = tiles;
      });
    };

    const asks = [];
    if (unknown.length) {
      const params = new URLSearchParams({ type: 'product', q: unknown.map((id) => `id:${id}`).join(' OR '), section_id: SECTION });
      asks.push(askTiles(`${root.dataset.searchUrl}?${params}`).then((tiles) => {
        const found = new Map(tiles.map((tile) => [tile.id, tile]));
        unknown.forEach((id) => {
          store.tiles[id] = found.get(id) || false; // false: sold out, a gift card or gone, not asked again
        });
      }));
    }
    if (!store.best && known + unknown.length < ROW) asks.push(askBest());
    await Promise.allSettled(asks);
    if (!store.best && !bestAsked && short()) await askBest().catch(() => {});

    save();
    return show(root, pick(store, ids, skip));
  }

  const busy = new WeakSet();
  const scheduled = new WeakSet();

  // Opening the dialog must paint before even a cached row reads styles or inserts tiles.
  // A resolved stylesheet promise only yields to a microtask, still inside the click's frame.
  function afterOpen(root) {
    if (scheduled.has(root)) return;
    scheduled.add(root);
    const fillIfOpen = () => {
      scheduled.delete(root);
      if (root.isConnected && root.closest('header-search')?.hasAttribute('open')) open(root);
    };
    requestAnimationFrame(() => setTimeout(fillIfOpen, 0));
  }

  function open(root) {
    sync(root);
    if (busy.has(root)) return;
    busy.add(root);
    fill(root)
      .catch(() => {
        if (root.dataset.state === 'waiting') paint(root, '', 'empty', '');
      })
      .finally(() => busy.delete(root));
  }

  /* Prestige's dialogs announce their opening on themselves, without bubbling: heard on the way down */
  document.addEventListener('dialog:before-show', (event) => {
    const root = event.target instanceof Element ? event.target.querySelector('[data-luxe-search-recent]') : null;
    if (root) afterOpen(root);
  }, true);

  document.addEventListener('input', (event) => {
    const root = event.target instanceof Element ? event.target.closest('header-search')?.querySelector('[data-luxe-search-recent]') : null;
    if (root) sync(root);
  });

  /* a search opened before this script ran */
  document.querySelectorAll('header-search[open] [data-luxe-search-recent]').forEach(afterOpen);
})();
